import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Megaphone } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { ModalLeituraComunicado } from "@/modules/comunicados/components/ModalLeituraComunicado";
import { useComunicadosRecentes, useMeusMinisteriosParticipo } from "@/modules/comunicados/hooks/useComunicados";
import type { ComunicadoComRelacoes } from "@/modules/comunicados/services/comunicadosService";
import { ministeriosSoPara } from "@/modules/comunicados/utils/destino";
import { rotuloPublicado } from "@/modules/comunicados/utils/prazo";

const LIMITE_PREVIEW = 2;

/**
 * Preview de Comunicados no topo da Início: os 2 mais recentes que a pessoa
 * pode ver (título e data) e o botão "Ver todos +N". Sem comunicados, a seção
 * some. A leitura abre na própria tela. Só aparece se a pessoa enxerga o elo.
 */
export function PreviewComunicadosHome() {
  const navigate = useNavigate();
  const { pessoa, modulosVisiveis } = useAuth();
  const veElo = modulosVisiveis.includes("comunicados");

  const { data, isLoading, isError, refetch } = useComunicadosRecentes(LIMITE_PREVIEW, veElo);
  const itens = data?.itens ?? [];
  const algumComDetalhes = itens.some((c) => c.detalhesVisiveis);
  const { data: meus } = useMeusMinisteriosParticipo(pessoa?.id, algumComDetalhes);
  const meusSet = meus ? new Set(meus) : undefined;

  const [aberto, setAberto] = useState<ComunicadoComRelacoes | null>(null);

  if (!veElo) return null;
  if (!isLoading && !isError && itens.length === 0) return null;

  const restantes = (data?.total ?? 0) - itens.length;

  return (
    <section
      aria-labelledby="preview-comunicados-titulo"
      className="mb-4 rounded-2xl border border-border bg-surface p-4 sm:p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 id="preview-comunicados-titulo" className="flex items-center gap-2 font-bold text-text">
          <Megaphone size={18} className="text-primary" /> Comunicados
        </h2>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2" aria-hidden="true">
          <div className="h-11 animate-pulse rounded-xl bg-surface-2" />
          <div className="h-11 animate-pulse rounded-xl bg-surface-2" />
        </div>
      ) : isError ? (
        <div className="rounded-xl bg-surface-2 p-4 text-center text-sm text-muted">
          <p className="font-semibold text-text">Não foi possível carregar</p>
          <p>Verifique a conexão e tente de novo.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 rounded-lg border border-border px-3 py-1.5 text-xs font-bold text-text hover:bg-surface"
          >
            Tentar de novo
          </button>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {itens.map((c) => {
              const soPara = ministeriosSoPara(c, meusSet);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setAberto(c)}
                    className="flex min-h-11 w-full items-center gap-2.5 rounded-xl border border-border bg-surface px-3 py-2 text-left transition-colors hover:bg-surface-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text">{c.titulo}</span>
                    <span className="shrink-0 text-xs text-muted">
                      {rotuloPublicado(c.publicado_em)}
                      {soPara ? ` · Só: ${soPara}` : ""}
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
          {restantes > 0 && (
            <button
              type="button"
              onClick={() => navigate("/comunicados")}
              className="mt-2.5 flex min-h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-primary/10 text-[13px] font-bold text-primary hover:bg-primary/15"
            >
              Ver todos
              <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-primary px-1.5 text-xs text-on-primary">
                +{restantes}
              </span>
            </button>
          )}
        </>
      )}

      <ModalLeituraComunicado
        comunicado={aberto}
        meusMinisterios={meusSet}
        podeEditar={false}
        onEditar={() => undefined}
        onFechar={() => setAberto(null)}
      />
    </section>
  );
}
